import classnames from 'classnames'
import React from 'react'
import { activatable } from './activatable'
import { useQuoteDocumentHeaderStyles } from './quoteDocumentHeaderStyles'

export interface HeaderLineLinkProps {
  /**
   * Omitted for a repeat of the line above it (a second email or phone): the icon's space is kept
   * blank so the value still lines up, but the icon is not drawn twice (ENG-2035).
   */
  icon?: React.ComponentType<{ className?: string }>
  onClick: () => void
  /** Keep the icon on the first line for multi-line values (e.g. a wrapped address). */
  alignTop?: boolean
  children: React.ReactNode
}

/**
 * One icon-prefixed detail line that deep-links on activation (tel:/mailto:/maps) — the repeated
 * building block of the contact / job-address / sales-rep columns. Clicks are kept from bubbling to the
 * enclosing column, so following the link never also opens that column's editor.
 *
 * The value ellipsizes, since these columns are only 200px wide at their flex basis (see
 * `truncate`). `alignTop` marks the multi-line ones, such as an address, which wrap instead.
 */
export default function HeaderLineLink({ icon: Icon, onClick, alignTop, children }: HeaderLineLinkProps) {
  const classes = useQuoteDocumentHeaderStyles()
  return (
    <span
      className={classnames(classes.lineItem, classes.lineLink, {
        [classes.lineItemTop]: alignTop,
        [classes.lineItemRepeat]: !Icon
      })}
      {...activatable(true, onClick, { stopPropagation: true })}
    >
      {Icon ? <Icon className={classes.lineIcon} /> : <span className={classes.lineIconSpacer} />}
      {/* Wrapped so the value clips independently of the icon beside it. `alignTop` marks the
          multi-line values, which get no truncation and wrap as before. */}
      <span className={alignTop ? undefined : classes.truncate}>{children}</span>
    </span>
  )
}
